import { useEffect, useState } from "react";
import { MenuItem, InputAdornment, CircularProgress } from "@mui/material";
import DTextField from "components/common/DTextField";

export default function SelectDataField({
    resource,
    value,
    onChange,
    label,
    required,
    displayField = "name",
    valueField = "id",
    error,
    filterValues,
    addEmpty,
    emptyLabel,
    defaultOptions = [],
    fetchData = false,
    ...rest
}) {
    const [items, setItems] = useState(defaultOptions);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setItems(defaultOptions);
    }, [defaultOptions]);

    useEffect(() => {
        if (items?.length || !fetchData) {
            setLoading(false);
            return;
        }
        const controller = new AbortController();

        (async () => {
            setLoading(true);
            try {
                const response = await resource.service.search(undefined, {
                    signal: controller.signal
                });

                let items = response.data;
                if (filterValues) {
                    items = filterValues(items);
                }
                setItems(items);
            } catch (err) {
                if (
                    err.name !== "CanceledError" &&
                    err.code !== "ERR_CANCELED"
                ) {
                    throw err;
                }
            } finally {
                if (!controller.signal.aborted) {
                    setLoading(false);
                }
            }
        })();

        return () => controller.abort();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const options = items.map((item) => (
        <MenuItem
            key={item[valueField]}
            value={item[valueField]}
            disabled={value === item[valueField]}
        >
            {item[displayField]}
        </MenuItem>
    ));
    if (addEmpty) {
        options.unshift(
            <MenuItem key="empty" value="">
                {emptyLabel || "Select..."}
            </MenuItem>
        );
    }
    return (
        <DTextField
            select
            fullWidth
            variant="outlined"
            label={label}
            value={value ?? ""}
            required={required}
            error={Boolean(error)}
            helperText={error || ""}
            disabled={loading}
            onChange={(e) => {
                const selected = items.find(
                    (item) =>
                        String(item[valueField]) === String(e.target.value)
                );
                onChange(selected || {});
            }}
            slotProps={{
                input: {
                    endAdornment: loading ? (
                        <InputAdornment position="end" sx={{ marginRight: 2 }}>
                            <CircularProgress size={18} />
                        </InputAdornment>
                    ) : undefined
                }
            }}
            sx={{
                marginBottom: "unset",
                marginTop: "unset",
                margin: "4px"
            }}
            {...rest}
        >
            {options}
        </DTextField>
    );
}
